/**
 * Postcode Checker v2 — Delivery Landing Page
 * Validates Canadian postal codes, checks FSA against eligible list,
 * shows inline messages with CTA buttons (no auto-redirect),
 * injects waitlist capture for out-of-zone users,
 * shows delivery day countdown for eligible users,
 * and includes exit-intent modal + form abandonment tracking.
 * No dependencies. Loaded with defer.
 * Binds to all [data-postcode-form] elements (supports multiple instances).
 */
(function () {
  'use strict';

  var UTM_PARAMS = '?utm_source=livraison&utm_medium=landing&utm_campaign=lancement';
  var ABANDON_TIMEOUT = 30000; // 30 seconds
  var SHOPIFY_API_VERSION = '2025-10'; // Update when Shopify retires this version (~12 months)
  var DAY_NAMES_FR = { 0: 'dimanche', 1: 'lundi', 2: 'mardi', 3: 'mercredi', 4: 'jeudi', 5: 'vendredi', 6: 'samedi' };
  var exitIntentFired = false;

  /* ----- FORM INIT ----- */
  function initForm(formEl) {
    var input = formEl.querySelector('[data-postcode-input]');
    var btn = formEl.querySelector('[data-postcode-submit]');
    var msgEl = formEl.querySelector('[data-postcode-message]');
    var fsaDataEl = document.getElementById('fge-eligible-fsas');

    if (!input || !btn || !fsaDataEl) return;

    var eligibleFSAs = [];
    try {
      eligibleFSAs = JSON.parse(fsaDataEl.textContent);
    } catch (e) {
      console.error('[FGE Postcode] FSA data parse error:', e);
      return;
    }

    // Auto-format: add space after 3rd character
    input.addEventListener('input', function () {
      var v = input.value.replace(/\s/g, '').toUpperCase().slice(0, 6);
      if (v.length > 3) {
        input.value = v.slice(0, 3) + ' ' + v.slice(3);
      } else {
        input.value = v;
      }
    });

    // Submit on button click
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      checkPostcode(input, msgEl, eligibleFSAs, formEl);
    });

    // Submit on Enter key
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        checkPostcode(input, msgEl, eligibleFSAs, formEl);
      }
    });

    // Form abandonment tracking (#14)
    var abandonTimer = null;
    input.addEventListener('focus', function () {
      abandonTimer = setTimeout(function () {
        fireAnalytics('form_abandoned', '');
      }, ABANDON_TIMEOUT);
    });

    input.addEventListener('blur', function () {
      if (abandonTimer) clearTimeout(abandonTimer);
    });

    btn.addEventListener('click', function () {
      if (abandonTimer) clearTimeout(abandonTimer);
    });
  }

  /* ----- PREFIX MATCHING — supports 3-char FSA + granular 4-6 char prefixes ----- */
  function matchesPrefix(postalCode, prefixes) {
    // postalCode = "J0E1Z0" (6 chars, no space)
    // prefixes = ["J2G", "J0E1Z", "J0E2N", ...] (3-6 chars)
    // Returns the matched prefix or null
    for (var i = 0; i < prefixes.length; i++) {
      var p = prefixes[i];
      if (postalCode.substring(0, p.length) === p) {
        return p;
      }
    }
    return null;
  }

  /* ----- MAIN CHECK ----- */
  function checkPostcode(input, msgEl, fsas, formEl) {
    var raw = input.value.replace(/\s/g, '').toUpperCase();

    // Validate Canadian postal code format: A1A1A1
    var valid = /^[A-Z]\d[A-Z]\d[A-Z]\d$/.test(raw);
    if (!valid) {
      showMessage(msgEl, 'error', 'Entrez un code postal valide (ex.\u00a0: J0J 1E0).');
      fireAnalytics('invalid', '');
      return;
    }

    var fsa = raw.substring(0, 3);

    // Loading state — disable button, show checking text
    var btn = formEl.querySelector('[data-postcode-submit]');
    var originalBtnText = '';
    if (btn) {
      originalBtnText = btn.textContent;
      btn.textContent = 'V\u00e9rification\u2026';
      btn.classList.add('dlv-postcode__btn--loading');
      btn.disabled = true;
    }
    if (input) input.disabled = true;

    // Brief delay for perceived responsiveness (avoids flash)
    setTimeout(function () {
      // Restore button state
      if (btn) {
        btn.textContent = originalBtnText;
        btn.classList.remove('dlv-postcode__btn--loading');
        btn.disabled = false;
      }
      if (input) input.disabled = false;

      // Prefix matching: try full code against prefix list (longest match wins)
      var matched = matchesPrefix(raw, fsas);
      if (matched) {
        handleEligible(raw, msgEl, formEl);
      } else {
        handleIneligible(fsa, msgEl, formEl);
      }
    }, 400);
  }

  /* ----- ELIGIBLE: Replace form with clean success state ----- */
  function handleEligible(postalCode, msgEl, formEl) {
    var fsa = postalCode.substring(0, 3);
    fireAnalytics('eligible', fsa);

    // Build segment-aware shop URL
    var activeTile = document.querySelector('.dlv-selector__tile--active');
    var seg = activeTile ? activeTile.dataset.dlvTile : '';
    var shopUrl = seg ? '/collections/' + seg + UTM_PARAMS : '/collections' + UTM_PARAMS;

    // Delivery day line (supports multi-zone: "mardi, mercredi")
    var countdown = getNextDeliveryInfo(postalCode);
    var dayLine = '';
    if (countdown) {
      if (countdown.multiZone) {
        dayLine = '<p class="dlv-success__detail">Livraison possible\u00a0: <strong>' + countdown.day + '</strong></p>';
      } else {
        dayLine = '<p class="dlv-success__detail">Prochaine livraison\u00a0: <strong>' + countdown.day + '</strong></p>';
      }
    }

    // Hide all form elements — works for hero, footer, AND sticky CTA
    var hideEls = formEl.querySelectorAll(
      '.dlv-postcode__row, .dlv-postcode__label, [data-postcode-input], [data-postcode-submit]'
    );
    hideEls.forEach(function (el) { el.style.display = 'none'; });
    if (msgEl) msgEl.style.display = 'none';

    clearInjectedBlocks(formEl);

    var successBlock = document.createElement('div');
    successBlock.className = 'dlv-postcode__success-block';
    successBlock.innerHTML =
      '<div class="dlv-success__confirmed">\u2713 On livre chez vous\u00a0!</div>' +
      dayLine +
      '<a href="' + shopUrl + '" class="dlv-postcode__success-btn">Commencer ma commande \u2192</a>' +
      '<button type="button" class="dlv-postcode__recheck-link">V\u00e9rifier un autre code \u2192</button>';

    formEl.appendChild(successBlock);

    // Focus management (B3): move focus to success block for screen readers
    successBlock.setAttribute('tabindex', '-1');
    successBlock.focus({ preventScroll: true });

    // Track shop CTA click
    var shopBtn = successBlock.querySelector('.dlv-postcode__success-btn');
    if (shopBtn) {
      shopBtn.addEventListener('click', function () {
        fireAnalytics('shop_cta_click', fsa);
      });
    }

    // Re-check: restore form, remove success block
    var recheckBtn = successBlock.querySelector('.dlv-postcode__recheck-link');
    if (recheckBtn) {
      recheckBtn.addEventListener('click', function () {
        hideEls.forEach(function (el) { el.style.display = ''; });
        if (msgEl) msgEl.style.display = '';
        successBlock.remove();
        var input = formEl.querySelector('[data-postcode-input]');
        if (input) { input.value = ''; input.focus(); }
      });
    }
  }

  /* ----- INELIGIBLE: Waitlist capture (#2) ----- */
  function handleIneligible(fsa, msgEl, formEl) {
    showMessage(msgEl, 'out-of-zone', '');
    fireAnalytics('ineligible', fsa);

    // Build waitlist HTML (includes CASL consent checkbox per F1+F2)
    var waitlistBlock = document.createElement('div');
    waitlistBlock.className = 'dlv-waitlist';
    waitlistBlock.innerHTML =
      '<p class="dlv-waitlist__heading">Pas encore dans votre secteur \u2014 mais on grandit\u00a0!</p>' +
      '<p class="dlv-waitlist__text">Inscrivez-vous pour \u00eatre notifi\u00e9 quand on livrera chez vous.</p>' +
      '<div class="dlv-waitlist__form">' +
        '<input type="email" class="dlv-waitlist__email" placeholder="votre@courriel.com" autocomplete="email" inputmode="email" required>' +
        '<label class="dlv-waitlist__consent">' +
          '<input type="checkbox" class="dlv-waitlist__consent-check" required>' +
          ' J\u2019accepte de recevoir des communications de F.G.\u00a0Edwards. ' +
          '<a href="/policies/privacy-policy" target="_blank" rel="noopener" class="dlv-waitlist__privacy-link">Politique de confidentialit\u00e9</a>' +
        '</label>' +
        '<button type="button" class="dlv-waitlist__submit">Me notifier</button>' +
      '</div>' +
      '<div class="dlv-waitlist__msg" aria-live="polite"></div>' +
      '<p class="dlv-waitlist__fallback">' +
        'Ou appelez-nous\u00a0: <a href="tel:+14502633251" class="dlv-waitlist__phone">(450) 263-3251</a><br>' +
        '<a href="/pages/contact">Visitez-nous \u00e0 West Brome \u2192</a>' +
      '</p>';

    clearInjectedBlocks(formEl);
    formEl.appendChild(waitlistBlock);

    // Bind waitlist submit
    var emailInput = waitlistBlock.querySelector('.dlv-waitlist__email');
    var consentCheck = waitlistBlock.querySelector('.dlv-waitlist__consent-check');
    var submitBtn = waitlistBlock.querySelector('.dlv-waitlist__submit');
    var msgArea = waitlistBlock.querySelector('.dlv-waitlist__msg');

    submitBtn.addEventListener('click', function () {
      var email = emailInput.value.trim();
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
        msgArea.innerHTML = '<span style="color:#a20901;font-size:1.3rem;">Entrez une adresse courriel valide.</span>';
        return;
      }
      if (!consentCheck.checked) {
        msgArea.innerHTML = '<span style="color:#a20901;font-size:1.3rem;">Veuillez accepter les communications pour continuer.</span>';
        return;
      }
      submitBtn.disabled = true;
      submitBtn.textContent = 'Envoi\u2026';
      submitWaitlist(email, fsa, msgArea, submitBtn);
    });
  }

  /* ----- WAITLIST FALLBACK (shared by no-token + fetch-error) ----- */
  function showWaitlistFallback(msgArea, fsa) {
    msgArea.innerHTML = '<span class="dlv-waitlist__success">Merci\u00a0! ' +
      'Envoyez-nous un courriel \u00e0 <a href="mailto:info@fgedwards.ca?subject=Liste%20attente%20livraison%20' + fsa +
      '&body=Mon%20code%20postal%3A%20' + fsa + '">info@fgedwards.ca</a></span>';
    fireAnalytics('waitlist_signup_fallback', fsa);
  }

  /* ----- SHOPIFY CUSTOMER API — Waitlist Submit ----- */
  function submitWaitlist(email, fsa, msgArea, btn) {
    var apiToken = document.querySelector('[data-api-token]');
    var token = apiToken ? apiToken.dataset.apiToken : '';

    if (!token) {
      showWaitlistFallback(msgArea, fsa);
      return;
    }

    // Use GraphQL variables to prevent injection
    var mutation = 'mutation($input: CustomerCreateInput!) { customerCreate(input: $input) { customer { id } customerUserErrors { message } } }';
    var variables = {
      input: {
        email: email,
        tags: ['waitlist-livraison', 'fsa-' + fsa],
        acceptsMarketing: true,
        note: 'waitlist-fsa:' + fsa
      }
    };

    // Abort after 15 seconds
    var controller = new AbortController();
    var timeout = setTimeout(function () { controller.abort(); }, 15000);

    fetch('/api/' + SHOPIFY_API_VERSION + '/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token
      },
      body: JSON.stringify({ query: mutation, variables: variables }),
      signal: controller.signal
    })
    .then(function (res) { return res.json(); })
    .then(function (data) {
      var errors = data && data.data && data.data.customerCreate && data.data.customerCreate.customerUserErrors;
      if (errors && errors.length > 0 && errors[0].message.indexOf('taken') !== -1) {
        msgArea.innerHTML = '<span class="dlv-waitlist__success">Vous \u00eates d\u00e9j\u00e0 inscrit\u00a0! On vous contactera bient\u00f4t.</span>';
      } else {
        msgArea.innerHTML = '<span class="dlv-waitlist__success">Merci\u00a0! On vous contactera d\u00e8s qu\u2019on livrera dans votre secteur.</span>';
      }
      btn.textContent = 'Inscrit \u2713';
      fireAnalytics('waitlist_signup', fsa);
    })
    .catch(function () {
      showWaitlistFallback(msgArea, fsa);
      // Re-enable button so user can retry (B2 fix)
      btn.disabled = false;
      btn.textContent = 'Me notifier';
    })
    .finally(function () {
      clearTimeout(timeout);
    });
  }

  /* ----- ZONE LOOKUP — prefix-based, supports multi-zone ----- */
  function lookupZones(postalCode, zoneMap) {
    // zoneMap keys can be 3-6 char prefixes
    // Returns array of zone names that match, longest prefix first
    var matched = [];
    var code = postalCode.replace(/\s/g, '').toUpperCase();
    var bestLen = 0;

    for (var prefix in zoneMap) {
      if (!zoneMap.hasOwnProperty(prefix)) continue;
      if (code.substring(0, prefix.length) === prefix) {
        var zoneVal = zoneMap[prefix];
        var zones = Array.isArray(zoneVal) ? zoneVal : [zoneVal];
        // Prefer longer (more specific) prefixes
        if (prefix.length > bestLen) {
          bestLen = prefix.length;
          matched = zones;
        } else if (prefix.length === bestLen) {
          // Same length — merge zones
          for (var i = 0; i < zones.length; i++) {
            if (matched.indexOf(zones[i]) === -1) matched.push(zones[i]);
          }
        }
      }
    }
    return matched;
  }

  /* ----- DELIVERY DAY COUNTDOWN (#10) — prefix-based, multi-zone ----- */
  function getNextDeliveryInfo(postalCode) {
    var zoneMapEl = document.getElementById('fge-zone-map');
    var dayMapEl = document.getElementById('fge-delivery-days');
    if (!zoneMapEl || !dayMapEl) return null;

    try {
      var zoneMap = JSON.parse(zoneMapEl.textContent);
      var dayMap = JSON.parse(dayMapEl.textContent);
      var zones = lookupZones(postalCode, zoneMap);
      if (zones.length === 0) return null;

      var now = new Date();
      var today = now.getDay();
      var deliveries = [];

      for (var i = 0; i < zones.length; i++) {
        var targetDay = dayMap[zones[i]];
        if (targetDay === undefined) continue;
        var daysUntil = (targetDay - today + 7) % 7;
        if (daysUntil === 0) daysUntil = 7;
        deliveries.push({ day: targetDay, daysUntil: daysUntil, zone: zones[i] });
      }

      if (deliveries.length === 0) return null;

      // Sort by soonest
      deliveries.sort(function (a, b) { return a.daysUntil - b.daysUntil; });

      // Build day names list (deduplicated, sorted by soonest)
      var seen = {};
      var dayNames = [];
      for (var j = 0; j < deliveries.length; j++) {
        var name = DAY_NAMES_FR[deliveries[j].day] || '';
        if (name && !seen[name]) {
          seen[name] = true;
          dayNames.push(name);
        }
      }

      return {
        day: dayNames.join(', ') || 'bient\u00f4t',
        daysUntil: deliveries[0].daysUntil,
        zone: deliveries[0].zone,
        multiZone: deliveries.length > 1
      };
    } catch (e) {
      return null;
    }
  }

  /* ----- EXIT-INTENT MODAL (#12) ----- */
  var exitIntentHandler = null;

  function initExitIntent() {
    exitIntentHandler = function (e) {
      if (e.clientY <= 0 && !exitIntentFired) {
        showExitModal();
      }
    };
    document.addEventListener('mouseout', exitIntentHandler);
  }

  function showExitModal() {
    var modal = document.getElementById('dlv-exit-modal');
    if (!modal) return;
    exitIntentFired = true;

    // Remove exit-intent listener — it only fires once
    if (exitIntentHandler) {
      document.removeEventListener('mouseout', exitIntentHandler);
      exitIntentHandler = null;
    }

    modal.classList.add('dlv-exit-modal--visible');
    fireAnalytics('exit_intent_shown', '');

    // Close handlers
    var closeBtn = modal.querySelector('.dlv-exit-modal__close');
    var overlay = modal.querySelector('.dlv-exit-modal__overlay');

    function escHandler(e) {
      if (e.key === 'Escape') closeModal();
    }

    function closeModal() {
      modal.classList.remove('dlv-exit-modal--visible');
      document.removeEventListener('keydown', escHandler);
      if (closeBtn) closeBtn.removeEventListener('click', closeModal);
      if (overlay) overlay.removeEventListener('click', closeModal);
    }

    if (closeBtn) closeBtn.addEventListener('click', closeModal);
    if (overlay) overlay.addEventListener('click', closeModal);
    document.addEventListener('keydown', escHandler);
  }

  /* ----- UTILITIES ----- */
  function showMessage(el, type, text) {
    if (!el) return;
    el.textContent = text;
    el.className = 'dlv-postcode__message dlv-postcode__message--' + type;
    el.setAttribute('aria-live', 'polite');
    el.removeAttribute('hidden');
  }

  function clearInjectedBlocks(formEl) {
    var existing = formEl.querySelectorAll('.dlv-postcode__success-block, .dlv-waitlist');
    existing.forEach(function (el) { el.remove(); });
  }

  function fireAnalytics(result, fsa) {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'delivery_zone_check',
        result: result,
        fsa: fsa
      });
    }
  }

  /* ----- NEXT-DELIVERY COUNTDOWN (passive urgency) ----- */
  function initDeliveryCountdown() {
    var dayMapEl = document.getElementById('fge-delivery-days');
    if (!dayMapEl) return;

    try {
      var dayMap = JSON.parse(dayMapEl.textContent);
    } catch (e) {
      return;
    }

    // Find the soonest delivery day across ALL zones
    var now = new Date();
    var today = now.getDay();
    var soonest = null;
    var soonestDays = 8;

    for (var zone in dayMap) {
      if (!dayMap.hasOwnProperty(zone)) continue;
      var targetDay = dayMap[zone];
      var daysUntil = (targetDay - today + 7) % 7;
      if (daysUntil === 0) daysUntil = 7;
      if (daysUntil < soonestDays) {
        soonestDays = daysUntil;
        soonest = targetDay;
      }
    }

    if (soonest === null) return;

    var dayName = DAY_NAMES_FR[soonest] || '';
    if (!dayName) return;

    // Capitalize first letter
    var dayCapital = dayName.charAt(0).toUpperCase() + dayName.slice(1);

    // Calculate order cutoff (day before delivery)
    var cutoffDay = (soonest - 1 + 7) % 7;
    var cutoffName = DAY_NAMES_FR[cutoffDay] || '';

    // Build urgency text
    var urgencyText = 'Prochaine livraison\u00a0: ' + dayCapital;
    if (soonestDays === 1) {
      urgencyText += ' (demain!)';
    } else if (cutoffName) {
      urgencyText += ' \u2014 commandez avant ' + cutoffName + ' minuit';
    }

    // Inject into countdown containers (hero + sticky CTA)
    var targets = document.querySelectorAll('[data-delivery-countdown]');
    targets.forEach(function (el) {
      el.textContent = urgencyText;
      el.removeAttribute('hidden');
    });
  }

  /* ----- INIT ALL ----- */
  function initAll() {
    var forms = document.querySelectorAll('[data-postcode-form]');
    forms.forEach(initForm);
    initExitIntent();
    initDeliveryCountdown();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
